// Context-Objekt + Hook getrennt vom Provider.
//
// `react-refresh/only-export-components` meldet zu Recht: exportiert eine Datei neben der
// Komponente auch noch Konstanten oder Funktionen, kann Vite beim Hot-Reload nicht mehr
// entscheiden, ob der Modulzustand erhalten bleiben darf — der Fast Refresh fällt dann auf
// einen vollen Reload zurück und der Anwendungszustand ist weg. Deshalb liegt hier alles,
// was KEINE Komponente ist; AuthContext.tsx exportiert nur noch den Provider.
import { createContext, useContext } from 'react';
import type { User, LoginCredentials, RegisterData } from '../types';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Wie `useAuth`, wirft aber nicht ohne `AuthProvider` — dann `undefined`.
 *
 * Für Bausteine, die die Rolle nur LESEN, um etwas wegzulassen (`useDarfModerieren`): die Formulare
 * werden in ihren Tests ohne Anbieter gerendert, und ein Formular soll nicht an einem Anbieter hängen,
 * nur um ein Feld zu sperren. Wer die Sitzung braucht, nimmt weiter `useAuth`.
 */
export const useAuthOptional = (): AuthContextType | undefined => useContext(AuthContext);
